/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import moment from 'moment';
import { PRODUCTION } from '../constants/environment';

const wsApi = process.env.REACT_APP_WS_API;

type WSContextType = {
  isConnected: boolean;
  ws: WebSocket | null;
};
const WSContext = createContext({ isConnected: false, ws: null } as WSContextType);

const WithWebSockets = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [reTryIndex, setReTryIndex] = useState<number>(0);

  // If user is trying to reconnect - we need to track down already created timeOuts
  // To avoid unnecessary tries
  const timeoutRef = useRef<null | NodeJS.Timeout>(null);

  const wsToken = useSelector((store: RootState) => store.user.tokens.ws);

  // If server is dead - user internet is dead or something else
  // WS connection will be closed - but user can get internet back up
  // so we need to try to reconnect after some time until we will succeed
  const tryToReconnect = () => {
    process.env.REACT_APP_ENV !== PRODUCTION && console.log('Trying to reconnect', moment(Date.now()).format('ddd MMM DD YYYY HH:MM:ss:SSSSSSS Z'));
    const timeout = setTimeout(() => {
      // readyState 3 = CLOSED connection
      // If old connection is closed - we retry
      // If open we don't do anything
      // Otherwise we will have endless loop because of useEffect where we close connection
      setWs((oldWs) => {
        if (oldWs?.readyState === 3 || oldWs?.readyState === 2) {
          setReTryIndex(reTryIndex + 1);
        }
        return oldWs;
      });
    }, parseInt(process.env.REACT_APP_PING_INTERVAL || '5000'));
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = timeout;
  };

  useEffect(() => {
    let connection: WebSocket | null = null;

    if (wsToken) {
      connection = new WebSocket(`${wsApi}?token=${wsToken}`);

      setWs(connection);
      connection.onopen = () => {
        process.env.REACT_APP_ENV !== PRODUCTION && console.log('websocket opened', moment(Date.now()).format('ddd MMM DD YYYY HH:MM:ss:SSSSSSS Z'));
        setIsConnected(true);
      };

      connection.onclose = () => {
        process.env.REACT_APP_ENV !== PRODUCTION && console.log('websocket closed', moment(Date.now()).format('ddd MMM DD YYYY HH:MM:ss:SSSSSSS Z'));
        tryToReconnect();
      };

      // FOR MAYBE FUTURE USE
      // connection.addEventListener('message', (e: any) => {
      //   const newData: any = JSON.parse(e.data);
      //   if (newData.type === 'ping') {
      //   }
      // });
      // const interval = setInterval(() => {
      //   connection?.send(JSON.stringify(
      //     {
      //       type: 'ping',
      //     },
      //   ));
      // }, parseInt(process.env.REACT_APP_PING_INTERVAL || '') || 1000);

      return () => {
        // clearInterval(interval);
        if (connection?.readyState === 1) connection?.close();
      };
    }
    return () => {};
  }, [wsToken, reTryIndex]);

  return (
    <WSContext.Provider value={{ isConnected, ws }}>
      { children }
    </WSContext.Provider>
  );
};

const useWebSockets = () => useContext(WSContext);

export default {
  WithWebSockets,
  useWebSockets,
};
